import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  FiArrowUpRight,
  FiCheck,
  FiLayers,
  FiMousePointer,
  FiZap,
} from "react-icons/fi";
import { useInView } from "react-intersection-observer";
import { typography, spacing, components } from "../styles/designSystem";
import { IN_VIEW_OPTIONS, reveal } from "../styles/animations";
import { socialLinks } from "../data/socialLinks";
import { whyO3SItems, fitCards } from "../data/aboutData";
import { CTA } from "../sections/CTA";

const SoftLogo = ({ className = "" }) => (
  <img
    src="/images/brand/o3s-logo.png"
    alt=""
    aria-hidden="true"
    className={`pointer-events-none select-none object-contain opacity-[0.045] grayscale brightness-125 contrast-75 ${className}`}
  />
);

const TiltSurface = ({ children, className = "", intensity = 5 }) => {
  const surfaceRef = useRef(null);

  const handleMove = (event) => {
    const element = surfaceRef.current;

    if (!element) return;

    const rect = element.getBoundingClientRect();
    const x = (event.clientX - rect.left) / rect.width - 0.5;
    const y = (event.clientY - rect.top) / rect.height - 0.5;

    element.style.setProperty("--mouse-x", `${50 + x * 35}%`);
    element.style.setProperty("--mouse-y", `${50 + y * 35}%`);

    element.style.transform = `perspective(1200px) rotateX(${
      -y * intensity
    }deg) rotateY(${x * intensity}deg) translateY(-4px)`;
  };

  const resetMove = () => {
    const element = surfaceRef.current;

    if (!element) return;

    element.style.setProperty("--mouse-x", "50%");
    element.style.setProperty("--mouse-y", "50%");

    element.style.transform =
      "perspective(1200px) rotateX(0deg) rotateY(0deg) translateY(0)";
  };

  return (
    <div
      ref={surfaceRef}
      onMouseMove={handleMove}
      onMouseLeave={resetMove}
      className={`cursor-default transition-transform duration-300 ease-out [transform-style:preserve-3d] ${className}`}
    >
      {children}
    </div>
  );
};

// ─── Founder Hero ────────────────────────────────────────────────────────────

const FounderHeroSection = ({
  sectionRef,
  visible,
  photoRef,
  handleMouseMove,
  resetTransform,
}) => (
  <section
    ref={sectionRef}
    className={`${reveal(visible)} relative mb-28 md:mb-36`}
  >
    <div className="pointer-events-none absolute -left-32 top-10 h-72 w-72 rounded-full bg-primary/[0.06] blur-3xl" />
    <div className="pointer-events-none absolute -right-40 bottom-0 h-72 w-72 rounded-full bg-accent/[0.12] blur-3xl" />

    <div className="relative grid items-center gap-14 lg:grid-cols-[1.08fr,0.92fr] lg:gap-20">
      {/* Introduction */}
      <div className="relative z-10">
        <div className="mb-7 flex items-center gap-3">
          <p className={`${typography.label} text-primary`}>Meet the founder</p>
        </div>

        <h1 className={`${typography.heading.h1} max-w-4xl text-secondary`}>
          Meet the founder behind{" "}
          <span className="relative inline-block font-semibold text-primary">
            O3S
            <span className="absolute -bottom-1 left-0 h-2 w-full rounded-full bg-accent/20" />
          </span>
        </h1>

        <div className={`${typography.body.large} mt-8 max-w-2xl space-y-5`}>
          <p>
            Arianne Roselina Prananto is a{" "}
            <span className="font-medium text-secondary">
              computer scientist and developer
            </span>{" "}
            from Indonesia, studying and working in Germany. She founded O3S to
            help small businesses build a clear and professional digital
            presence without unnecessary agency complexity.
          </p>

          <p>
            Her work combines technical thinking, thoughtful design, and
            practical business strategy through one direct and collaborative
            process. Clients never need to feel “techy” before asking for
            support.
          </p>
        </div>

        <div className="mt-9 flex flex-wrap gap-3">
          <a href="#what-i-believe" className={components.button.primary}>
            Discover the idea behind O3S
            <FiArrowUpRight className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>

          <Link
            to="/contact"
            className={`${components.button.secondary} px-7 py-4`}
          >
            Start a conversation
            <FiArrowUpRight className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </div>
      </div>

      {/* Founder portrait */}
      <div className="relative mx-auto w-full max-w-md lg:max-w-none">
        <div className="animate-pulse-slow pointer-events-none absolute -left-6 -top-5 h-20 w-20 rotate-12 rounded-[1.5rem] bg-primary/[0.10]" />

        <div className="pointer-events-none absolute -right-8 top-14 h-28 w-28 rounded-full border-[12px] border-accent/20" />

        <div className="pointer-events-none absolute -bottom-7 left-12 h-16 w-16 rotate-45 rounded-xl bg-accent/[0.18]" />

        <div className="relative rotate-[1.5deg] rounded-[2.7rem] border border-secondary/10 bg-[#ebd5ab]/40 p-4 shadow-2xl">
          <div
            ref={photoRef}
            onMouseMove={handleMouseMove}
            onMouseLeave={resetTransform}
            className="relative overflow-hidden rounded-[2rem] bg-gray-200 shadow-xl transition-transform duration-300"
          >
            <img
              src="/images/brand/arianne-roselina-prananto.jpg"
              alt="Arianne Roselina Prananto, founder of O3S"
              className="aspect-[4/4.5] w-full object-cover"
            />

            <div className="pointer-events-none absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-secondary/45 to-transparent" />
          </div>

          <div className="px-3 pb-3 pt-6">
            <div className="flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <h2 className="text-xl font-semibold text-secondary">
                  Arianne Roselina Prananto
                </h2>

                <p className="mt-1 text-sm font-medium text-primary">
                  Founder, O3S, Germany
                </p>
              </div>

              <div className="flex gap-2">
                {socialLinks.map((social) => (
                  <a
                    key={social.label}
                    href={social.href}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={social.label}
                    className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-secondary/10 bg-white text-secondary transition-all duration-300 hover:-translate-y-1 hover:border-primary/30 hover:bg-primary hover:text-white hover:shadow-lg"
                  >
                    <social.Icon className="h-4 w-4" />
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// ─── Full-Width Belief Section ───────────────────────────────────────────────

const BeliefSection = ({ sectionRef, visible }) => (
  <section
    id="what-i-believe"
    ref={sectionRef}
    className={`${reveal(
      visible,
    )} relative mb-32 w-full overflow-hidden bg-secondary text-white md:mb-40`}
  >
    <div className="pointer-events-none absolute -left-32 top-1/2 h-80 w-80 -translate-y-1/2 rounded-full bg-primary/20 blur-[100px]" />
    <div className="pointer-events-none absolute -right-20 -top-24 h-80 w-80 rounded-full bg-accent/10 blur-[100px]" />
    <div className="pointer-events-none absolute right-0 top-0 select-none text-[11rem] font-semibold leading-none text-white/[0.025] md:text-[18rem]">
      BELIEVE
    </div>

    <SoftLogo className="absolute -right-10 bottom-0 h-52 w-52 md:h-72 md:w-72 lg:h-96 lg:w-96" />

    <div className={`${spacing.container} relative py-20 md:py-28 lg:py-32`}>
      <div className="grid gap-14 lg:grid-cols-[0.72fr,1.28fr] lg:gap-20">
        <div>
          <p className={`${typography.label} mt-7 text-white/50`}>
            What I believe
          </p>

          <div className="mt-7 h-px w-20 bg-gradient-to-r from-accent to-transparent" />
        </div>

        <div>
          <h2 className={`${typography.heading.h2} max-w-4xl text-white`}>
            Small and new businesses deserve the same{" "}
            <span className="font-semibold text-accent">
              fighting chance to grow.
            </span>
          </h2>

          <p className="mt-8 max-w-3xl text-base leading-8 text-white/70 md:text-lg">
            You already have enough on your plate when starting and growing a
            business. Your digital presence should build trust and help people
            choose you without adding unnecessary complexity.
          </p>
        </div>
      </div>
    </div>
  </section>
);

// ─── How I Work ──────────────────────────────────────────────────────────────

const WorkingStyleSection = ({ sectionRef, visible }) => (
  <section
    ref={sectionRef}
    className={`${reveal(visible)} relative mb-32 md:mb-40`}
  >
    <div className="relative mb-14 grid gap-8 lg:grid-cols-[0.55fr,1.45fr] lg:gap-16">
      <div>
        <p className={`${typography.label} text-primary`}>How I work</p>

        <div className="mt-5 h-1 w-14 rounded-full bg-gradient-to-r from-primary to-accent" />
      </div>

      <div>
        <h2 className={`${typography.heading.h2} max-w-4xl text-secondary`}>
          Honest input is more useful than a{" "}
          <span className="font-semibold text-primary">perfect brief.</span>
        </h2>
      </div>
    </div>

    <div className="relative overflow-hidden rounded-[2.7rem] border border-secondary/10 bg-[#fff7ec] shadow-xl">
      <div className="pattern-diagonal-lines pointer-events-none absolute -right-12 -top-12 h-56 w-56 rounded-[3rem] opacity-60" />

      <div className="pointer-events-none absolute -bottom-16 -left-16 h-56 w-56 rounded-full bg-primary/[0.06] blur-3xl" />

      <div className="relative px-7 py-12 md:px-12 md:py-16 lg:px-16">
        <span className="block text-8xl font-semibold leading-[0.65] text-primary/15">
          “
        </span>

        <p className="mt-2 max-w-4xl text-2xl font-medium leading-10 text-secondary md:text-3xl md:leading-[1.45]">
          I work best with founders who are honest about where they’re at. You
          don’t need the perfect plan, just a direction.
        </p>
      </div>
    </div>
  </section>
);

// ─── Fit Slider ──────────────────────────────────────────────────────────────

const FitSlider = () => {
  const [value, setValue] = useState(50);

  const isDIY = value < 30;
  const isBigAgency = value > 70;
  const isDanger = isDIY || isBigAgency;

  const state = isDIY
    ? {
        label: "DIY approach",
        title: "Doing everything yourself",
        text: "You keep full control, but strategy, design, content, and technical decisions can quickly consume your time and slow the project down.",
        Icon: FiMousePointer,
      }
    : isBigAgency
      ? {
          label: "Big agency approach",
          title: "Hiring a large agency",
          text: "You receive extensive support, but may also pay for larger teams, additional meetings, account management, and processes your project does not need.",
          Icon: FiLayers,
        }
      : {
          label: "O3S approach",
          title: "Direct support without agency overload",
          text: "You get strategy, design, and technical execution through one direct partner, without having to manage everything yourself or hire a large agency.",
          Icon: FiCheck,
        };

  const StateIcon = state.Icon;

  return (
    <TiltSurface
      intensity={3}
      className="group relative mt-12 overflow-hidden rounded-[2rem] border border-secondary/10 bg-white p-7 shadow-xl md:p-8"
    >
      <div className="pattern-diagonal-lines pointer-events-none absolute -right-8 -top-8 h-40 w-40 rounded-3xl opacity-80" />

      <div className="relative">
        <div className="flex flex-col justify-between gap-4 md:flex-row md:items-end">
          <div>
            <p className={`${typography.label} text-primary`}>
              Interactive fit check
            </p>

            <h3 className={`${typography.card.large} mt-2`}>
              How much support does your project need?
            </h3>

            <p className={`${typography.body.xs} mt-2`}>
              Move from doing everything yourself to working with a large
              agency. O3S is designed as the practical middle.
            </p>
          </div>

          <div
            className={`inline-flex shrink-0 items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold ${
              isDanger ? "bg-red/10 text-red" : "bg-primary/10 text-primary"
            }`}
          >
            <StateIcon size={16} />
            {state.label}
          </div>
        </div>

        <div className="mt-8">
          <div className="mb-4 grid grid-cols-3 items-end gap-3">
            <p className="text-sm font-semibold text-red">DIY</p>

            <p className="text-center text-sm font-semibold text-primary">
              O3S
            </p>

            <p className="text-right text-sm font-semibold text-red">
              Big Agency
            </p>
          </div>

          <input
            type="range"
            min="0"
            max="100"
            value={value}
            onChange={(event) => setValue(Number(event.target.value))}
            aria-label="Choose between DIY, O3S, and a big agency"
            className="fit-range"
            style={{
              "--thumb-color": isDanger ? "#9c1d14" : "#e67e22",
            }}
          />

          <div className="mt-4 grid grid-cols-3 text-xs font-medium">
            <span className={isDIY ? "text-red" : "text-gray-400"}>
              Everything on you
            </span>

            <span
              className={`text-center ${
                !isDanger ? "font-semibold text-primary" : "text-gray-400"
              }`}
            >
              Balanced support
            </span>

            <span
              className={`text-right ${
                isBigAgency ? "text-red" : "text-gray-400"
              }`}
            >
              Full agency process
            </span>
          </div>
        </div>

        <div
          className={`mt-8 rounded-3xl border p-6 transition-colors duration-300 ${
            isDanger
              ? "border-red/30 bg-red/[0.04]"
              : "border-primary/20 bg-primary/[0.04]"
          }`}
        >
          <div className="flex flex-col gap-5 md:flex-row md:items-center md:justify-between">
            <div>
              <h3
                className={`${typography.card.large} ${
                  isDanger ? "text-red" : "text-secondary"
                }`}
              >
                {state.title}
              </h3>

              <p className="mt-3 max-w-2xl text-sm leading-6 text-gray-600">
                {state.text}
              </p>
            </div>

            <div
              className={`flex h-16 w-16 shrink-0 items-center justify-center rounded-3xl ${
                isDanger ? "bg-red/15 text-red" : "bg-primary text-white"
              }`}
            >
              <StateIcon size={26} />
            </div>
          </div>
        </div>
      </div>
    </TiltSurface>
  );
};

// ─── When O3S Fits ───────────────────────────────────────────────────────────

const WhenO3SFitsSection = ({ sectionRef, visible }) => (
  <section
    id="when-o3s-fits"
    ref={sectionRef}
    className={`${reveal(
      visible,
    )} relative mb-32 overflow-hidden rounded-[2.7rem] bg-accent/[0.10] px-6 py-12 shadow-sm md:mb-40 md:px-10 md:py-16 lg:px-12`}
  >
    <div className="pattern-cross-grid pointer-events-none absolute -right-4 -top-4 h-40 w-40 rounded-2xl opacity-60" />

    <div className="pointer-events-none absolute -bottom-8 -left-8 h-20 w-20 rotate-45 rounded-xl bg-primary/[0.06]" />

    <div className="relative">
      <div className="grid gap-14 lg:grid-cols-[0.82fr,1.18fr] lg:gap-16">
        <div>
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary text-white">
            <FiZap size={20} />
          </div>

          <p className={`${typography.label} mt-6 text-primary`}>
            When O3S makes sense
          </p>

          <h2 className={`${typography.heading.h2} mt-4 text-secondary`}>
            You need momentum,{" "}
            <span className="font-semibold text-primary">not chaos.</span>
          </h2>

          <p className={`${typography.body.base} mt-5 max-w-xl`}>
            O3S is designed for businesses that need professional support
            without turning the project into a large and complicated agency
            process.
          </p>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          {fitCards.map((item, index) => {
            const Icon = item.Icon;

            return (
              <article
                key={item.title}
                className="animate-lift group relative overflow-hidden rounded-[2rem] bg-white p-6 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
                style={{
                  animationDelay: `${index * 450}ms`,
                  transitionDelay: `${index * 80}ms`,
                }}
              >
                <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/10 text-primary transition-transform duration-300 group-hover:rotate-6 group-hover:scale-110">
                  <Icon size={20} />
                </div>

                <h3
                  className={`${typography.card.base} mt-5 group-hover:text-primary`}
                >
                  {item.title}
                </h3>

                <p className={`${typography.body.small} mt-3`}>{item.desc}</p>
              </article>
            );
          })}
        </div>
      </div>

      <FitSlider />
    </div>
  </section>
);

// ─── Why Choose O3S ──────────────────────────────────────────────────────────

const WhyChooseUsSection = ({ sectionRef, visible }) => (
  <section
    ref={sectionRef}
    className={`${reveal(
      visible,
    )} relative mb-24 overflow-hidden rounded-[2.7rem] bg-secondary text-white shadow-2xl`}
  >
    <div
      className="absolute inset-0 bg-cover bg-center"
      style={{ backgroundImage: "url('/images/people-discussing.jpg')" }}
    />

    <div className="absolute inset-0 bg-black/[0.91]" />

    <div className="absolute inset-0 bg-gradient-to-br from-secondary/20 via-transparent to-primary/25" />

    <div className="relative grid gap-12 p-8 md:grid-cols-[0.8fr,1.2fr] md:p-12 lg:p-14">
      <div className="space-y-7">
        <div>
          <p className={`mb-3 ${typography.label} text-white/55`}>
            Why O3S is different
          </p>

          <h2 className={`${typography.heading.h2} text-white`}>
            One partner.{" "}
            <span className="font-semibold text-accent">
              One clear direction.
            </span>
          </h2>
        </div>

        <div className="h-1 w-14 rounded-full bg-gradient-to-r from-primary to-accent" />

        <p className={`${typography.body.base} max-w-md text-white/80`}>
          Work directly with one reliable partner through a streamlined, fully
          online process.
        </p>
      </div>

      <div className="flex flex-col gap-3">
        {whyO3SItems.map((item, index) => (
          <article
            key={item.title}
            className={`group relative overflow-hidden rounded-2xl border border-white/10 bg-white/[0.08] p-5 backdrop-blur-sm transition-all duration-300 hover:-translate-y-1 hover:border-accent/30 hover:bg-white/[0.14] ${
              index % 2 === 1 ? "lg:ml-8" : "lg:mr-8"
            }`}
          >
            <span className="absolute right-4 top-1/2 -translate-y-1/2 text-6xl font-semibold text-white/[0.04]">
              0{index + 1}
            </span>

            <div className="relative flex items-start gap-4">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-accent/20 text-accent transition-transform duration-300 group-hover:rotate-6 group-hover:scale-110">
                <FiCheck size={19} />
              </div>

              <div>
                <h3 className={`${typography.heading.h4} text-white`}>
                  {item.title}
                </h3>

                <p
                  className={`${typography.body.small} mt-1.5 max-w-xl text-white/70`}
                >
                  {item.text}
                </p>
              </div>
            </div>
          </article>
        ))}
      </div>
    </div>
  </section>
);

// ─── Main Page ───────────────────────────────────────────────────────────────

const AboutPage = () => {
  const { ref: founderRef, inView: founderInView } = useInView(IN_VIEW_OPTIONS);

  const { ref: beliefRef, inView: beliefInView } = useInView(IN_VIEW_OPTIONS);

  const { ref: workingStyleRef, inView: workingStyleInView } =
    useInView(IN_VIEW_OPTIONS);

  const { ref: fitRef, inView: fitInView } = useInView(IN_VIEW_OPTIONS);

  const { ref: differenceRef, inView: differenceInView } =
    useInView(IN_VIEW_OPTIONS);

  const { ref: ctaRef, inView: ctaInView } = useInView(IN_VIEW_OPTIONS);

  const photoRef = useRef(null);

  const handleMouseMove = (event) => {
    const element = photoRef.current;

    if (!element) return;

    const rect = element.getBoundingClientRect();
    const x = (event.clientX - rect.left) / rect.width - 0.5;
    const y = (event.clientY - rect.top) / rect.height - 0.5;

    element.style.transform = `perspective(1000px) rotateX(${
      -y * 7
    }deg) rotateY(${x * 7}deg) rotateZ(-2deg) scale(1.02)`;
  };

  const resetTransform = () => {
    if (!photoRef.current) return;

    photoRef.current.style.transform =
      "perspective(1000px) rotateX(0) rotateY(0) rotateZ(-2deg) scale(1)";
  };

  return (
    <main className="relative min-h-screen overflow-hidden bg-white pt-24 md:pt-32">
      <div className={`${spacing.container} relative z-10 pt-20 md:pt-28`}>
        <FounderHeroSection
          sectionRef={founderRef}
          visible={founderInView}
          photoRef={photoRef}
          handleMouseMove={handleMouseMove}
          resetTransform={resetTransform}
        />
      </div>

      {/* Full-width belief statement */}
      <BeliefSection sectionRef={beliefRef} visible={beliefInView} />

      <div className={`${spacing.container} relative z-10 pb-24 md:pb-32`}>
        <WorkingStyleSection
          sectionRef={workingStyleRef}
          visible={workingStyleInView}
        />

        <WhenO3SFitsSection sectionRef={fitRef} visible={fitInView} />

        <WhyChooseUsSection
          sectionRef={differenceRef}
          visible={differenceInView}
        />

        <CTA
          sectionRef={ctaRef}
          visible={ctaInView}
          title="Ready to work together?"
          subtitle="Tell us where your business is today. We'll figure out the right next step together."
        />
      </div>
    </main>
  );
};

export default AboutPage;
